import { DynamicForm } from "@shared/components/DynamicForm";
import { useToast } from "@shared/hooks/use-toast";
import { FormField, FormService } from "@shared/services/FormServices";
import { Button } from "@shared/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { Loader2, Package } from "lucide-react";
import React, { useCallback, useEffect, useMemo } from "react";
import { useAddSopMaterial, useEditSopMaterial } from "../../../../hooks";
import { useCurrency } from "@shared/selectors/globalSelectors";

interface Material {
  name: string;
  size?: string;
  quantity_required: string; // Changed to match API response
  cost: string;
  quantity: string;
}

interface MaterialFormData {
  name: string;
  size: string;
  quantity: string;
  cost: string;
}

interface AddMaterialModalProps {
  isOpen: boolean;
  onClose: () => void;
  sopId: string;
}

interface EditMaterialModalProps {
  isOpen: boolean;
  onClose: () => void;
  sopId: string;
  material: Material | null;
  materialIndex: number;
}

// Add Material Modal Component
export const AddMaterialModal: React.FC<AddMaterialModalProps> = ({
  isOpen,
  onClose,
  sopId,
}) => {
    const currency = useCurrency();
  const { toast } = useToast();
  const addMaterialMutation = useAddSopMaterial();

  // Form fields configuration
  const formFields: FormField[] = useMemo(
    () => [
      {
        name: "name",
        type: "text",
        label: "Material Name",
        placeholder: "Enter material name...",
        required: true,
        minLength: 2,
        maxLength: 100,
        helperText: "Provide a clear name for the material",
      },
      {
        name: "size",
        type: "text",
        label: "Size/Dimensions",
        placeholder: "e.g., 10mm, Large, 50x30cm",
        required: false,
        maxLength: 50,
        helperText: "Optional size or dimensions specification",
      },
      {
        name: "quantity",
        type: "number",
        label: "Required Quantity",
        placeholder: "0",
        required: true,
        min: 0,
        step: 1,
        helperText: "Required quantity of the material",
      },
      {
        name: "cost",
        type: "number",
        label: `Cost (${currency})`,
        placeholder: "0.00",
        required: true,
        min: 0,
        step: 0.01,
        helperText: "Cost per unit of the material",
      },
    ],
    []
  );

  // Create form instance using FormService
  const form = FormService.createForm({
    fields: formFields,
    mode: "onChange",
    validateOnMount: false,
    defaultValues: {
      name: "",
      size: "",
      quantity: "0",
      cost: "0",
    },
  });

  // Handle field changes
  const handleFieldChange = useCallback((fieldName: string, value: any) => {
    // Optional: Add any side effects here if needed
  }, []);

  // Handle form submission
  const handleSubmit = async (data: MaterialFormData) => {
    try {
      await addMaterialMutation.mutateAsync({
        sopId,
        materialData: {
          name: data.name.trim(),
          size: data.size ? data.size.trim() : undefined,
          quantity_required: data.quantity,
          cost: data.cost,
        },
      });

      toast({
        title: "Success",
        description: `Material "${data.name}" added successfully`,
        variant: "default",
      });

      // Reset form and close modal
      FormService.resetForm(form, formFields);
      onClose();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to add material",
        variant: "destructive",
      });
    }
  };

  const handleReset = useCallback(() => {
    FormService.resetForm(form, formFields);
  }, [form, formFields]);

  const handleClose = useCallback(() => {
    FormService.resetForm(form, formFields);
    onClose();
  }, [form, formFields, onClose]);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      FormService.resetForm(form, formFields);
    }
  }, [isOpen, form, formFields]);

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="h-5 w-5 text-blue-600" />
            Add New Material
          </DialogTitle>
          <DialogDescription>
            Add a new material requirement to this SOP template. Materials are
            items needed to complete the SOP tasks.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 pr-4">
          <DynamicForm
            fields={formFields}
            form={form}
            onSubmit={handleSubmit}
            onReset={handleReset}
            onFieldChange={handleFieldChange}
            loading={addMaterialMutation.isPending}
            gridColumns={2}
            responsiveColumns={{
              sm: 1,
              md: 2,
            }}
            config={{
              showResetButton: false,
              showSubmitButton: false,
              resetOnSubmit: false,
              classes: {
                form: "space-y-4",
                container: "space-y-2",
                label:
                  "block text-sm font-medium text-gray-900 mb-1 flex justify-between",
                input:
                  "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed",
                select:
                  "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white disabled:bg-gray-100 disabled:cursor-not-allowed",
                error: "text-sm text-red-600 mt-1",
                helperText: "text-xs text-muted-foreground mt-1",
                required: "text-red-500 ml-1",
              },
            }}
            renderCustomFooter={() => (
              <div className="flex justify-end gap-2 pt-4">
                <Button
                  variant="outline"
                  onClick={handleClose}
                  disabled={addMaterialMutation.isPending}
                >
                  Cancel
                </Button>
                <Button
                  onClick={form.handleSubmit(handleSubmit)}
                  disabled={addMaterialMutation.isPending}
                >
                  {addMaterialMutation.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Adding Material...
                    </>
                  ) : (
                    "Add Material"
                  )}
                </Button>
              </div>
            )}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Edit Material Modal Component
export const EditMaterialModal: React.FC<EditMaterialModalProps> = ({
  isOpen,
  onClose,
  sopId,
  material,
}) => {
  const { toast } = useToast();
  const editMaterialMutation = useEditSopMaterial();

  // Form fields configuration (same as Add modal)
  const formFields: FormField[] = useMemo(
    () => [
      {
        name: "name",
        type: "text",
        label: "Material Name",
        placeholder: "Enter material name...",
        required: true,
        minLength: 2,
        maxLength: 100,
        helperText: "Provide a clear name for the material",
      },
      {
        name: "size",
        type: "text",
        label: "Size/Dimensions",
        placeholder: "e.g., 10mm, Large, 50x30cm",
        required: false,
        maxLength: 50,
        helperText: "Optional size or dimensions specification",
      },
      {
        name: "quantity",
        type: "number",
        label: "Required Quantity",
        placeholder: "0",
        required: true,
        min: 0,
        step: 1,
        helperText: "Required quantity of the material",
      },
      {
        name: "cost",
        type: "number",
        label: "Cost (per unit)",
        placeholder: "0.00",
        required: true,
        min: 0,
        step: 0.01,
        helperText: "Cost per unit of the material",
      },
    ],
    []
  );

  // Create form instance using FormService
  const form = FormService.createForm({
    fields: formFields,
    mode: "onChange",
    validateOnMount: false,
    defaultValues: {
      name: "",
      size: "",
      quantity: "0",
      cost: "0",
    },
  });

  // Populate form when material changes
  useEffect(() => {
    if (material && isOpen) {
      const populateData = {
        name: material.name || "",
        size: material.size || "",
        quantity: material.quantity || "0", // Map quantity_required to quantity for form
        cost: material.cost || "0",
      };

      FormService.populateForm(form, populateData, formFields);
    }
  }, [material, isOpen, form, formFields]);

  // Handle field changes
  const handleFieldChange = useCallback((fieldName: string, value: any) => {
    // Optional: Add any side effects here if needed
  }, []);

  // Handle form submission
  const handleSubmit = async (data: MaterialFormData) => {
    if (!material) return;

    try {
      await editMaterialMutation.mutateAsync({
        sopId,
        existingValue: material.name, // Use original material name as existing value
        materialData: {
          name: data.name.trim(),
          size: data.size ? data.size.trim() : undefined,
          quantity: data.quantity,
          cost: data.cost,
        },
      });

      toast({
        title: "Success",
        description: `Material "${data.name}" updated successfully`,
        variant: "default",
      });

      onClose();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update material",
        variant: "destructive",
      });
    }
  };

  const handleReset = useCallback(() => {
    FormService.resetForm(form, formFields);
  }, [form, formFields]);

  const handleClose = useCallback(() => {
    FormService.resetForm(form, formFields);
    onClose();
  }, [form, formFields, onClose]);

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="h-5 w-5 text-blue-600" />
            Edit Material
          </DialogTitle>
          <DialogDescription>
            Update the material requirement details for this SOP template.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 pr-4">
          <DynamicForm
            fields={formFields}
            form={form}
            onSubmit={handleSubmit}
            onReset={handleReset}
            onFieldChange={handleFieldChange}
            loading={editMaterialMutation.isPending}
            gridColumns={2}
            responsiveColumns={{
              sm: 1,
              md: 2,
            }}
            config={{
              showResetButton: false,
              showSubmitButton: false,
              resetOnSubmit: false,
              classes: {
                form: "space-y-4",
                container: "space-y-2",
                label: "block text-sm font-medium text-gray-900 mb-1",
                input:
                  "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed",
                select:
                  "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white disabled:bg-gray-100 disabled:cursor-not-allowed",
                error: "text-sm text-red-600 mt-1",
                helperText: "text-xs text-muted-foreground mt-1",
                required: "text-red-500 ml-1",
              },
            }}
            renderCustomFooter={() => (
              <div className="flex justify-end gap-2 pt-4">
                <Button
                  variant="outline"
                  onClick={handleClose}
                  disabled={editMaterialMutation.isPending}
                >
                  Cancel
                </Button>
                <Button
                  onClick={form.handleSubmit(handleSubmit)}
                  disabled={editMaterialMutation.isPending}
                >
                  {editMaterialMutation.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Updating Material...
                    </>
                  ) : (
                    "Update Material"
                  )}
                </Button>
              </div>
            )}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};
